<template>
  <v-sheet class="day-panel" rounded="lg" outlined>
    <div class="day-panel__header">
      <div class="day-panel__title">{{ formattedDate }}</div>
      <v-btn color="#483CB9" dark small class="text-capitalize" @click="$emit('add')">
        <v-icon small left>mdi-calendar-plus</v-icon>
        Интервал
      </v-btn>
    </div>

    <div class="day-panel__section-title">Доступность</div>
    <div v-if="!availability.length" class="day-panel__empty">
      На эту дату нет открытых интервалов. Добавьте доступность эксперта.
    </div>
    <div v-else class="day-panel__list mb-4">
      <v-sheet
        v-for="item in sortedAvailability"
        :key="item.id"
        class="day-slot"
        outlined
        rounded="lg"
      >
        <div class="day-slot__top">
          <div class="day-slot__time">
            <v-icon small class="mr-1">mdi-clock-outline</v-icon>
            {{ timeRange(item) }}
          </div>
          <v-chip x-small color="#e3f6ec" text-color="#009E60">Свободен</v-chip>
        </div>

        <div class="day-slot__bottom">
          <div class="day-slot__expert" v-if="expertById(item.expert)">
            <v-avatar size="28" color="#483CB9" class="mr-2">
              <span class="white--text text-caption">{{ initials(expertFullName(item.expert)) }}</span>
            </v-avatar>
            <div class="day-slot__expert-name">{{ expertFullName(item.expert) }}</div>
          </div>
          <div>
            <v-btn icon x-small @click="$emit('edit-availability', item.id)">
              <v-icon small>mdi-pencil-outline</v-icon>
            </v-btn>
            <v-btn icon x-small @click="closeAvailability(item.id)">
              <v-icon small>mdi-close-circle-outline</v-icon>
            </v-btn>
          </div>
        </div>

        <div v-if="slotServiceChips(item).length" class="day-slot__services">
          <v-chip
            v-for="chip in slotServiceChips(item)"
            :key="chip.id"
            x-small
            outlined
            :color="chip.excluded ? '#bdbdbd' : '#483CB9'"
            :class="['mr-1', 'mt-1', { 'day-slot__service--excluded': chip.excluded }]"
            :title="chip.excluded ? 'На стопе в этом интервале' : ''"
          >
            {{ chip.name }}
          </v-chip>
        </div>
      </v-sheet>
    </div>

    <div class="d-flex align-center justify-space-between mb-2">
      <div class="day-panel__section-title mb-0">Записи</div>
      <v-btn-toggle v-if="cancelledAppointments.length" v-model="appointmentsTab" mandatory dense class="calendar-activity-toggle">
        <v-btn small value="active">Активные ({{ appointments.length }})</v-btn>
        <v-btn small value="cancelled">Отменённые ({{ cancelledAppointments.length }})</v-btn>
      </v-btn-toggle>
    </div>
    <div v-if="!shownAppointments.length" class="day-panel__empty">
      {{ appointmentsTab === 'cancelled' ? 'На эту дату отменённых/отклонённых записей нет.' : 'На эту дату записей нет.' }}
    </div>
    <div v-else>
      <AppointmentCard
        v-for="appointment in sortedShownAppointments"
        :key="appointment.id"
        :appointment="appointment"
        :loading="actionLoading"
        @accept="$emit('accept-appointment', $event)"
        @decline="$emit('decline-appointment', $event)"
        @edit-conference-link="$emit('edit-conference-link', $event)"
      />
    </div>
  </v-sheet>
</template>

<script>
import { utcToLocalTime } from '@/common/calendarTime';
import AppointmentCard from './AppointmentCard.vue';

export default {
  name: 'DaySlotsPanel',
  components: { AppointmentCard },
  props: {
    date: { type: String, required: true },
    availability: { type: Array, default: () => [] },
    appointments: { type: Array, default: () => [] },
    cancelledAppointments: { type: Array, default: () => [] },
    experts: { type: Array, default: () => [] },
    services: { type: Array, default: () => [] },
    serviceTypes: { type: Array, default: () => [] },
    actionLoading: { type: Boolean, default: false },
  },
  data() {
    return {
      appointmentsTab: 'active', // active | cancelled
    };
  },
  computed: {
    formattedDate() {
      return this.$moment(this.date, 'YYYY-MM-DD').locale('ru').format('D MMMM YYYY');
    },
    sortedAvailability() {
      return [...this.availability].sort((a, b) => a.starts_at.localeCompare(b.starts_at));
    },
    shownAppointments() {
      return this.appointmentsTab === 'cancelled' ? this.cancelledAppointments : this.appointments;
    },
    sortedShownAppointments() {
      return [...this.shownAppointments].sort((a, b) => a.scheduled_starts_at.localeCompare(b.scheduled_starts_at));
    },
  },
  watch: {
    date() {
      this.appointmentsTab = 'active';
    },
  },
  methods: {
    expertById(id) {
      return this.experts.find((expert) => expert.id === id) || null;
    },
    expertFullName(id) {
      const expert = this.expertById(id);
      if (!expert) return '';
      return `${expert.first_name || ''} ${expert.last_name || ''}`.trim();
    },
    timeRange(item) {
      return `${utcToLocalTime(item.starts_at)} – ${utcToLocalTime(item.ends_at)}`;
    },
    // Интервал доступности сам по себе не привязан к одной услуге (Availability не хранит
    // service_type) — в это время можно забронировать любую активную услугу эксперта,
    // поэтому показываем весь набор его активных услуг как подсказку, а не единственный тип.
    // Активные услуги эксперта с пометкой, исключена ли услуга из этого интервала
    // (availability.unavailable_services). Исключённые показываем серыми и зачёркнутыми.
    slotServiceChips(item) {
      const excluded = new Set(item.unavailable_services || []);
      return this.services
        .filter((service) => service.expert === item.expert && service.is_active !== false)
        .map((service) => {
          const serviceType = this.serviceTypes.find((type) => type.id === service.service_type);
          return {
            id: service.id,
            name: serviceType ? serviceType.name : `Услуга #${service.service_type}`,
            excluded: excluded.has(service.id),
          };
        });
    },
    initials(name) {
      return name.split(' ')
        .filter(Boolean)
        .map((part) => part[0])
        .slice(0, 2)
        .join('')
        .toUpperCase();
    },
    closeAvailability(id) {
      // eslint-disable-next-line no-alert
      if (window.confirm('Закрыть этот интервал доступности? Свободное время перестанет быть доступно для записи.')) {
        this.$emit('close-availability', id);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.calendar-activity-toggle .v-btn {
  text-transform: none;
}

.day-panel {
  padding: 16px;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
  }

  &__title {
    font-weight: 600;
    font-size: 16px;
    text-transform: capitalize;
  }

  &__section-title {
    font-size: 12px;
    font-weight: 600;
    color: #8a8f9c;
    text-transform: uppercase;
    margin-bottom: 8px;
  }

  &__empty {
    color: #8a8f9c;
    font-size: 13px;
    text-align: center;
    padding: 24px 8px;
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
}

.day-slot {
  padding: 12px;

  &__top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 4px;
  }

  &__time {
    display: flex;
    align-items: center;
    font-size: 13px;
    font-weight: 500;
  }

  &__bottom {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 8px;
  }

  &__expert {
    display: flex;
    align-items: center;
  }

  &__expert-name {
    font-size: 13px;
    font-weight: 500;
  }

  &__services {
    margin-top: 4px;
  }

  &__service--excluded {
    text-decoration: line-through;
  }
}
</style>
